import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const POLL_MS = 60000;

/**
 * How many notifications are unread right now, for the nav badge. Refetches on an
 * interval and whenever the tab regains focus, so a pending household approval (or
 * anything else in the existing /notifications feed) shows up without a full reload.
 * Only polls while signed in — the api client's own 401 interceptor already handles
 * a dropped session, so this just needs to stop asking once `user` is gone.
 */
export function useNotificationCount() {
    const { user } = useAuth();
    const [count, setCount] = useState(0);

    useEffect(() => {
        if (!user) {
            setCount(0);
            return;
        }
        let cancelled = false;

        const refresh = () => {
            api.get("/notifications")
                .then((res) => {
                    if (cancelled) return;
                    const items = Array.isArray(res.data) ? res.data : [];
                    setCount(items.filter((n) => !n.is_read).length);
                })
                .catch(() => {
                    // Background poll — a failed fetch (401, network blip) just leaves
                    // the badge as it was; the interceptor handles auth failure itself.
                });
        };

        refresh();
        const interval = setInterval(refresh, POLL_MS);
        const onVisible = () => {
            if (document.visibilityState === "visible") refresh();
        };
        document.addEventListener("visibilitychange", onVisible);

        return () => {
            cancelled = true;
            clearInterval(interval);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [user]);

    return count;
}
